import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useSnackbar } from "@/hooks/useSnackbar";
import { ENCOUNTER_SUMMARY_REPORT_QUERY_KEY } from "@/queries/encounter/use-encounter-summary-report";
import { useSignCosign } from "@/queries/encounter/use-sign-cosign";
import { useVerifyCosignPin } from "@/queries/use-verify-cosign-pin";
import { getErrorSnackbar, getXtenantId } from "@/utils/utils";
import { COSIGN_STEP, NOTE_PREVIEW_LABELS } from "../note-preview-constant";
import { CosignStep } from "../note-preview-types";
import { useCosignForm, UseCosignFormResult } from "./use-cosign-form";

interface Props {
  appointmentId?: string;
  patientName: string;
  onCosigned: () => void;
}

export interface UseCosignFlowResult {
  form: UseCosignFormResult;
  step: CosignStep;
  isVerifyingPin: boolean;
  isSigning: boolean;
  cancelStep: () => void;
  verifyPin: (pin: string) => Promise<boolean>;
  signWithSignature: (signature: string) => Promise<void>;
}

/** Save & Co-Sign → PIN check → signature → co-sign request; each step opens the next dialog. */
export const useCosignFlow = ({
  appointmentId,
  patientName,
  onCosigned,
}: Props): UseCosignFlowResult => {
  const [step, setStep] = useState<CosignStep>(COSIGN_STEP.IDLE);
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: verifyCosignPin, isPending: isVerifyingPin } =
    useVerifyCosignPin();
  const { mutateAsync: signCosign, isPending: isSigning } = useSignCosign();

  const form = useCosignForm({
    onSubmit: () => setStep(COSIGN_STEP.PIN),
  });

  const cancelStep = useCallback(() => setStep(COSIGN_STEP.IDLE), []);

  const verifyPin = useCallback(
    async (pin: string) => {
      try {
        const matches = await verifyCosignPin({
          pin,
          xTENANTID: getXtenantId(),
        });
        if (matches) setStep(COSIGN_STEP.SIGN);
        return matches;
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
        return false;
      }
    },
    [verifyCosignPin, showSnackbar]
  );

  const signWithSignature = useCallback(
    async (signature: string) => {
      if (!appointmentId) return;
      try {
        await signCosign({
          appointmentUuid: appointmentId,
          encounterCosign: {
            cosignText: form.note.trim(),
            cosignerSignature: signature,
          },
          xTENANTID: getXtenantId(),
        });
        showSnackbar({
          message: `${NOTE_PREVIEW_LABELS.cosignSuccessPrefix}${patientName}${NOTE_PREVIEW_LABELS.cosignSuccessSuffix}`,
          type: "success",
        });
        queryClient.invalidateQueries({
          queryKey: [ENCOUNTER_SUMMARY_REPORT_QUERY_KEY, appointmentId],
        });
        setStep(COSIGN_STEP.IDLE);
        onCosigned();
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
      }
    },
    [
      appointmentId,
      patientName,
      form.note,
      signCosign,
      showSnackbar,
      onCosigned,
    ]
  );

  return useMemo(
    () => ({
      form,
      step,
      isVerifyingPin,
      isSigning,
      cancelStep,
      verifyPin,
      signWithSignature,
    }),
    [
      form,
      step,
      isVerifyingPin,
      isSigning,
      cancelStep,
      verifyPin,
      signWithSignature,
    ]
  );
};
